export type StatsTab = 'frequency' | 'gaps' | 'history' | 'mine' | 'fair';

import type { MessageKey } from '../i18n/messages.ts';

export type StatsTabDef = { key: StatsTab; label: MessageKey };

/**
 * 标签顺序。**History 打头。**
 *
 * <p>原来打头的是 Frequency。绝大多数人点「Statistics」是要查号码——查完就走，
 * 冷热分布是少数人的第二诉求。把它排在第一位，等于每个人都要多点一次才到目的地。
 */
export const STATS_TABS: readonly StatsTabDef[] = [
  { key: 'history', label: 'milharTabHistory' },
  // 「我的」只对登录玩家渲染（MilharStatsPage 过滤）—— 访客点进来只会看到一句
  // 登录提示，把一个对他永远是空的标签摆在最显眼的第二位不合适。
  { key: 'mine', label: 'milharTabMine' },
  { key: 'frequency', label: 'milharTabFrequency' },
  { key: 'gaps', label: 'milharTabGaps' },
  { key: 'fair', label: 'milharTabFair' },
];

export const DEFAULT_STATS_TAB: StatsTab = 'history';

export type StatsRow = {
  digit: number;
  value: number;
  /** 柱长百分比，0–100。最大者恒为 100。 */
  barPct: number;
  /** 仅 `fair` tab 用；其余 tab 为 null。 */
  withinBand: boolean | null;
};

/**
 * 一位上的十个数字排成可渲染的行。视觉规格 §12A。
 *
 * <p><b>冷热不用颜色编码。</b>D4 的「色即语义」里青=活信号、金=钱、绿=赢、红=输/急，
 * 没有一个能表示「热」——用红蓝会污染整套语义。冷热只由**排序位置**和**柱长**表达。
 *
 * <p>`fair` tab **按数字顺序排**（0→9），因为它要对照理论值看分布，
 * 而不是找"最热的号"；排序本身会暗示排名，那正是这个 tab 要避免的读法。
 */
export function statsRows(
  values: readonly number[],
  tab: StatsTab,
  band: { low: number; high: number },
): StatsRow[] {
  const max = values.reduce((a, b) => Math.max(a, b), 0);
  const rows = values.map((value, digit) => ({
    digit,
    value,
    barPct: max > 0 ? (value / max) * 100 : 0,
    withinBand: tab === 'fair' ? value >= band.low && value <= band.high : null,
  }));
  if (tab === 'fair') return rows;
  // 最热在上。同值时按数字升序，避免同分行在每次刷新里乱跳
  return rows.sort((a, b) => (b.value - a.value) || (a.digit - b.digit));
}

/**
 * 反转排序（点表头时）。`fair` tab 不参与 —— 它按数字顺序排是固定的。
 */
export function toggleSorted(rows: readonly StatsRow[], descending: boolean): StatsRow[] {
  const sorted = [...rows].sort((a, b) => (a.value - b.value) || (a.digit - b.digit));
  return descending ? sorted.reverse() : sorted;
}

/**
 * 遗漏值的显示文本。
 *
 * <p>`gap === sample` 表示整个窗口都没出现过 —— 显示 `sample+` 而不是一个确切数字，
 * 因为我们只知道「至少这么久」，不知道到底多久。写死一个数会把不知道说成知道。
 */
export function gapText(gap: number, sample: number): string {
  return gap >= sample ? `${sample}+` : String(gap);
}
